(() => {
  'use strict';
  if (window.BlacklyteProductHorizontalCarousel) {
    window.BlacklyteProductHorizontalCarousel.initWithin(document);
    return;
  }
  const selector = '[data-product-horizontal-carousel]';
  const init = (root) => {

    if (!root || root.dataset.initialized === 'true') return;

    const viewport = root.querySelector('[data-product-horizontal-viewport]');
    const track = root.querySelector('[data-product-horizontal-track]');
    const cards = Array.from(root.querySelectorAll('[data-product-horizontal-card]'));
    const controls = root.querySelector('[data-product-horizontal-controls]');
    const previousButton = root.querySelector('[data-product-horizontal-previous]');
    const nextButton = root.querySelector('[data-product-horizontal-next]');
    if (!viewport || !track) return;

    const pagination = root.querySelector('[data-product-horizontal-pagination]');
    const navigation = root.querySelector('[data-product-horizontal-navigation]');
    const status = root.querySelector('[data-product-horizontal-status]');
    const controller = new AbortController();
    const signal = controller.signal;
    let wheelTimer;
    let layoutFrame;
    let pageButtons = [];

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileViewport = window.matchMedia('(max-width: 768px)');
    const canRemoveLeftSpacing = root.classList.contains('product-horizontal-showcase--no-left-spacing');
    let currentIndex = 0;
    let maxIndex = 0;
    let maxTranslate = 0;
    let cardStep = 0;
    let isDragging = false;
    let hasDragged = false;
    let pointerId = null;
    let startX = 0;
    let startTranslate = 0;
    let dragTranslate = 0;
    let wheelLocked = false;

    const getStep = () => {
      if (!cards[0]) return 0;
      const cardStyles = window.getComputedStyle(cards[0]);
      const trackStyles = window.getComputedStyle(track);
      const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
      const cardWidth = cards[0].getBoundingClientRect().width || Number.parseFloat(cardStyles.flexBasis) || 0;
      return cardWidth + gap;
    };

    const getCssLength = (propertyName) => {
      const value = window.getComputedStyle(root).getPropertyValue(propertyName).trim();
      const parsedValue = Number.parseFloat(value);
      if (!Number.isFinite(parsedValue)) return 0;
      if (value.endsWith('vw')) return (parsedValue / 100) * window.innerWidth;
      if (value.endsWith('vh')) return (parsedValue / 100) * window.innerHeight;
      return parsedValue;
    };

    const getLeadingSpacing = () => getCssLength('--product-horizontal-leading-space');

    const getEndSpacing = () => getCssLength('--product-horizontal-end-space');

    const getMaxTranslate = () => {
      // Measure the content inset, not an in-flight viewport width transition.
      // This is the showcase's shifted bound (natural overflow + leading - end),
      // expressed in the initial coordinate space, including short tracks.
      const viewportWidth = canRemoveLeftSpacing
        ? root.clientWidth - getLeadingSpacing()
        : viewport.clientWidth;
      const naturalMaxTranslate = Math.max(0, track.scrollWidth - viewportWidth);
      const isShifted = canRemoveLeftSpacing && root.dataset.productHorizontalShifted === 'true';
      return isShifted ? Math.max(0, naturalMaxTranslate - getEndSpacing()) : naturalMaxTranslate;
    };

    const syncViewportPosition = () => {
      if (!canRemoveLeftSpacing) return;
      root.dataset.productHorizontalShifted = currentIndex > 0 ? 'true' : 'false';
    };

    const updateControls = () => {
      const canMove = maxIndex > 0;
      if (controls) controls.hidden = !canMove;
      if (navigation) navigation.hidden = !canMove;

      if (previousButton) {
        previousButton.disabled = !canMove || currentIndex <= 0;
        previousButton.setAttribute('aria-disabled', String(previousButton.disabled));
      }

      if (nextButton) {
        nextButton.disabled = !canMove || currentIndex >= maxIndex;
        nextButton.setAttribute('aria-disabled', String(nextButton.disabled));
      }

      if (pagination) {
        pagination.hidden = !canMove;
        const pageCount = canMove ? maxIndex + 1 : 0;
        if (pageButtons.length !== pageCount) {
          pageButtons = Array.from({ length: pageCount }, (_, index) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'product-horizontal-showcase__page';
            button.dataset.productHorizontalPage = String(index);
            button.setAttribute('aria-label', 'Go to review page ' + (index + 1));
            button.setAttribute('aria-controls', viewport.id);
            return button;
          });
          pagination.replaceChildren(...pageButtons);
        }
        pageButtons.forEach((button, index) => {
          button.setAttribute('aria-current', String(index === currentIndex));
        });
      }
      if (status) status.textContent = 'Review page ' + (currentIndex + 1) + ' of ' + (maxIndex + 1);
      root.dataset.productHorizontalIndex = String(currentIndex);
    };

    const getTranslateForIndex = (index) => {
      // The final snap point must use the real scroll bound so the last card
      // reaches the preserved right-side end space instead of stopping one
      // leading inset short.
      if (maxIndex > 0 && index >= maxIndex) return maxTranslate;

      const leadingSpacing = canRemoveLeftSpacing && index > 0 ? getLeadingSpacing() : 0;
      return Math.min(Math.max(0, index * cardStep - leadingSpacing), maxTranslate);
    };

    const getDragTargetIndex = (translate) => {
      const thresholdRatio = mobileViewport.matches ? 0.25 : 0.5;
      let targetIndex = currentIndex;

      // Compare real snap-point gaps so the shorter final step uses the same threshold ratio.
      if (translate > startTranslate) {
        for (let index = currentIndex + 1; index <= maxIndex; index += 1) {
          const fromTranslate = getTranslateForIndex(index - 1);
          const toTranslate = getTranslateForIndex(index);
          const threshold = fromTranslate + (toTranslate - fromTranslate) * thresholdRatio;
          if (translate < threshold) break;
          targetIndex = index;
        }
      } else if (translate < startTranslate) {
        for (let index = currentIndex - 1; index >= 0; index -= 1) {
          const fromTranslate = getTranslateForIndex(index + 1);
          const toTranslate = getTranslateForIndex(index);
          const threshold = fromTranslate - (fromTranslate - toTranslate) * thresholdRatio;
          if (translate > threshold) break;
          targetIndex = index;
        }
      }

      return targetIndex;
    };

    const applyTranslate = (value, withTransition = true) => {
      track.style.transition = withTransition ? '' : 'none';
      track.style.transform = `translate3d(${(-value / window.innerWidth) * 100}vw, 0, 0)`;
    };

    const updateLayout = () => {
      syncViewportPosition();
      cardStep = getStep();
      maxTranslate = getMaxTranslate();
      maxIndex = cardStep && maxTranslate > 0 ? Math.ceil(maxTranslate / cardStep) : 0;
      const clampedIndex = Math.min(currentIndex, maxIndex);
      if (clampedIndex !== currentIndex) {
        currentIndex = clampedIndex;
        syncViewportPosition();
        maxTranslate = getMaxTranslate();
        maxIndex = cardStep && maxTranslate > 0 ? Math.ceil(maxTranslate / cardStep) : 0;
        currentIndex = Math.min(currentIndex, maxIndex);
      }
      isDragging = false;
      hasDragged = false;
      pointerId = null;
      track.classList.remove('is-dragging');
      applyTranslate(getTranslateForIndex(currentIndex), false);
      updateControls();
    };

    const goTo = (index, withTransition = true) => {
      currentIndex = Math.max(0, index);
      syncViewportPosition();
      cardStep = getStep();
      maxTranslate = getMaxTranslate();
      maxIndex = cardStep && maxTranslate > 0 ? Math.ceil(maxTranslate / cardStep) : 0;
      currentIndex = Math.min(currentIndex, maxIndex);
      if (currentIndex === 0) {
        syncViewportPosition();
        maxTranslate = getMaxTranslate();
        maxIndex = cardStep && maxTranslate > 0 ? Math.ceil(maxTranslate / cardStep) : 0;
      }
      applyTranslate(getTranslateForIndex(currentIndex), withTransition && !prefersReducedMotion.matches);
      updateControls();
    };

    const handleViewportKeydown = (event) => {
      let nextIndex = null;
      if (event.key === 'ArrowRight') nextIndex = currentIndex + 1;
      if (event.key === 'ArrowLeft') nextIndex = currentIndex - 1;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = maxIndex;
      if (nextIndex === null) return;

      event.preventDefault();
      goTo(nextIndex);
    };

    const handleViewportWheel = (event) => {
      if (maxTranslate <= 0.01) return;

      const wheelDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (!wheelDelta) return;

      const direction = wheelDelta > 0 ? 1 : -1;
      const canMove = direction > 0 ? currentIndex < maxIndex : currentIndex > 0;
      if (!canMove) return;

      event.preventDefault();
      if (wheelLocked) return;

      wheelLocked = true;
      goTo(currentIndex + direction);
      wheelTimer = window.setTimeout(() => {
        wheelLocked = false;
      }, 350);
    };

    const handlePointerDown = (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest?.('[data-product-horizontal-controls]')) return;

      isDragging = true;
      hasDragged = false;
      pointerId = event.pointerId;
      startX = event.clientX;
      startTranslate = getTranslateForIndex(currentIndex);
      dragTranslate = startTranslate;
    };

    const handlePointerMove = (event) => {
      if (!isDragging || (pointerId !== null && event.pointerId !== pointerId)) return;

      const delta = event.clientX - startX;
      const dragActivationThreshold = Math.max(6, 0.5 * (window.innerWidth / 100));
      if (!hasDragged && Math.abs(delta) <= dragActivationThreshold) return;

      if (!hasDragged) {
        hasDragged = true;
        track.classList.add('is-dragging');
        viewport.setPointerCapture?.(event.pointerId);
      }

      dragTranslate = Math.max(0, Math.min(maxTranslate, startTranslate - delta));
      applyTranslate(dragTranslate, false);
      event.preventDefault();
    };

    const finishDrag = (event) => {
      if (!isDragging || (pointerId !== null && event.pointerId !== pointerId)) return;

      isDragging = false;
      track.classList.remove('is-dragging');
      if (viewport.hasPointerCapture?.(event.pointerId)) viewport.releasePointerCapture(event.pointerId);

      if (hasDragged) goTo(event.type === 'pointercancel' ? currentIndex : getDragTargetIndex(dragTranslate));
      pointerId = null;
    };

    const handleClick = (event) => {
      if (!hasDragged) return;
      event.preventDefault();
      event.stopPropagation();
      hasDragged = false;
    };

    const scheduleLayout = () => {
      window.cancelAnimationFrame(layoutFrame);
      layoutFrame = window.requestAnimationFrame(updateLayout);
    };
    const onPrevious = () => goTo(currentIndex - 1);
    const onNext = () => goTo(currentIndex + 1);
    const onPage = (event) => {
      const button = event.target.closest('[data-product-horizontal-page]');
      if (button && pagination.contains(button)) goTo(Number(button.dataset.productHorizontalPage));
    };
    const onBlockSelect = (event) => {
      const blockId = event.detail && event.detail.blockId;
      const index = cards.findIndex((card) => card.dataset.blockId === blockId || card.querySelector('[data-block-id]')?.dataset.blockId === blockId);
      if (index >= 0) goTo(index, false);
    };

    root.dataset.initialized = 'true';
    updateLayout();
    window.addEventListener('resize', scheduleLayout, { passive: true, signal });
    viewport.addEventListener('keydown', handleViewportKeydown, { signal });
    viewport.addEventListener('wheel', handleViewportWheel, { passive: false, signal });
    viewport.addEventListener('pointerdown', handlePointerDown, { signal });
    viewport.addEventListener('pointermove', handlePointerMove, { passive: false, signal });
    viewport.addEventListener('pointerup', finishDrag, { signal });
    viewport.addEventListener('pointercancel', finishDrag, { signal });
    viewport.addEventListener('click', handleClick, { capture: true, signal });
    previousButton?.addEventListener('click', onPrevious, { signal });
    nextButton?.addEventListener('click', onNext, { signal });
    pagination?.addEventListener('click', onPage, { signal });
    root.addEventListener('shopify:block:select', onBlockSelect, { signal });
    prefersReducedMotion.addEventListener('change', scheduleLayout, { signal });
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(scheduleLayout) : null;
    resizeObserver?.observe(root);
    viewport.querySelectorAll('a, img').forEach((element) => { element.draggable = false; });
    root.productHorizontalCleanup = () => {
      controller.abort();
      resizeObserver?.disconnect();
      window.clearTimeout(wheelTimer);
      window.cancelAnimationFrame(layoutFrame);
      delete root.dataset.initialized;
    };
  };
  const rootsWithin = (scope) => [
    ...(scope.matches?.(selector) ? [scope] : []),
    ...scope.querySelectorAll(selector),
  ];
  const initWithin = (scope) => rootsWithin(scope).forEach(init);
  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target).forEach((root) => root.productHorizontalCleanup?.());
  });
  window.BlacklyteProductHorizontalCarousel = { initWithin };
  initWithin(document);
})();
